import React, { useState, useMemo, useEffect } from 'react';
import { useDiaperStore } from '../stores/diaperStore';
import { useSessionsStore } from '../stores/sessionsStore';
import { DiaperModel } from '../lib/types';
import { ConfigAccount, accountDisplayLabel, loadEnabledConfigAccounts } from '../lib/novaAccounts';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleLinear } from '@visx/scale';
import { LinePath } from '@visx/shape';
import { curveMonotoneX } from '@visx/curve';
import { GridRows, GridColumns } from '@visx/grid';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';

// Generate wetness points from 0 to 32767 for the curve
const generateCurveData = (c0: number, c1: number, c2: number, c3: number, capacity: number) => {
  const points = [];
  const maxWetness = 32767;
  const step = Math.floor(maxWetness / 100);

  for (let w = 0; w <= maxWetness; w += step) {
    let vol = c0 + c1 * w + c2 * Math.pow(w, 2) + c3 * Math.pow(w, 3);
    // Mimic the backend constraint: volume cannot be negative, and usually caps at capacity
    vol = Math.max(0, vol);
    points.push({ w, v: vol });
  }
  // Ensure we hit the max exactly
  let finalVol = c0 + c1 * maxWetness + c2 * Math.pow(maxWetness, 2) + c3 * Math.pow(maxWetness, 3);
  points.push({ w: maxWetness, v: Math.max(0, finalVol) });
  
  return points;
};

const tooltipStyles = {
  ...defaultStyles,
  backgroundColor: 'rgba(0,0,0,0.9)',
  color: 'white',
  padding: '8px',
  borderRadius: '4px',
};

export const DiaperSandboxPage: React.FC = () => {
  const { diaperModels, localModelOverrides, loadDiaperModels, saveLocalOverride, deleteLocalOverride } = useDiaperStore();
  const currentNovaAccountId = useSessionsStore((s) => s.currentNovaAccountId);
  const setCurrentNovaAccountId = useSessionsStore((s) => s.setCurrentNovaAccountId);

  const [selectedBaseCode, setSelectedBaseCode] = useState<number | null>(null);
  const [configAccounts, setConfigAccounts] = useState<ConfigAccount[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  
  const [expName, setExpName] = useState('My Experimental Model');
  const [c0, setC0] = useState(0);
  const [c1, setC1] = useState(0);
  const [c2, setC2] = useState(0);
  const [c3, setC3] = useState(0);
  const [capacity, setCapacity] = useState(2000);

  // Diaper-models is global to a Nova install but the proxy is account-aware
  // (it needs JSESSIONID credentials), so the user has to pick which account
  // to drive. Load enabled accounts; if exactly one, auto-select it.
  useEffect(() => {
    let cancelled = false;
    loadEnabledConfigAccounts()
      .then((accounts) => {
        if (cancelled) return;
        setConfigAccounts(accounts);
        setAccountsError(null);
        if (!currentNovaAccountId && accounts.length === 1) {
          setCurrentNovaAccountId(accounts[0].id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load Nova accounts:', err);
        setAccountsError(err?.message || 'Failed to load Nova accounts');
      });
    return () => { cancelled = true; };
  }, [currentNovaAccountId, setCurrentNovaAccountId]);

  useEffect(() => {
    return () => setCurrentNovaAccountId(null);
  }, [setCurrentNovaAccountId]);

  useEffect(() => {
    if (!currentNovaAccountId) return;
    loadDiaperModels(currentNovaAccountId).catch(console.error);
  }, [currentNovaAccountId, loadDiaperModels]);

  // Set initial selected model
  useEffect(() => {
    if (diaperModels.length > 0 && selectedBaseCode === null) {
      const initial = diaperModels[0];
      setSelectedBaseCode(initial.code);
      setC0(initial.c0);
      setC1(initial.c1);
      setC2(initial.c2);
      setC3(initial.c3);
      setCapacity(initial.capacity);
      setExpName(`${initial.name} (Exp)`);
    }
  }, [diaperModels, selectedBaseCode]);

  const baseModel = useMemo(() => {
    return diaperModels.find(m => m.code === selectedBaseCode) || null;
  }, [diaperModels, selectedBaseCode]);

  const handleBaseModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = parseInt(e.target.value, 10);
    setSelectedBaseCode(code);
    const model = diaperModels.find(m => m.code === code);
    if (model) {
      setC0(model.c0);
      setC1(model.c1);
      setC2(model.c2);
      setC3(model.c3);
      setCapacity(model.capacity);
      setExpName(`${model.name} (Exp)`);
    }
  };

  const handleSaveOverride = () => {
    if (!baseModel) return;
    
    // Generate a unique code > 1000 for local overrides to avoid clashing with real codes
    const newCode = Math.max(10000, ...localModelOverrides.map(m => m.code), ...diaperModels.map(m => m.code)) + 1;
    
    const newModel: DiaperModel = {
      ...baseModel,
      code: newCode,
      name: expName,
      capacity: capacity,
      c0, c1, c2, c3,
    };
    
    saveLocalOverride(newModel);
    alert('Experimental model saved! You can now select it in the Clip Monitor.');
  };

  const baseData = useMemo(() => {
    if (!baseModel) return [];
    return generateCurveData(baseModel.c0, baseModel.c1, baseModel.c2, baseModel.c3, baseModel.capacity);
  }, [baseModel]);

  const expData = useMemo(() => {
    return generateCurveData(c0, c1, c2, c3, capacity);
  }, [c0, c1, c2, c3, capacity]);

  // Chart Tooltip setup
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip<{ w: number, baseV: number, expV: number }>();

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold mb-2">🧪 Diaper Model Sandbox</h2>
        <p className="text-gray-600 text-sm">
          Visualize how the Wetness Level (0-32767) translates to Volume (mL) using the 3rd degree polynomial.
          Tweak the coefficients to create experimental models and save them locally to use in the Clip Monitor.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
          <label htmlFor="sandbox-account" className="text-sm font-medium text-gray-700 sm:min-w-[140px]">
            Nova Account:
          </label>
          <select
            id="sandbox-account"
            className="border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-blue-500 sm:max-w-md flex-1"
            value={currentNovaAccountId || ''}
            onChange={(e) => setCurrentNovaAccountId(e.target.value || null)}
          >
            <option value="">— Select an account to load Nova diaper models —</option>
            {configAccounts.map((acc) => (
              <option key={acc.id} value={acc.id}>{accountDisplayLabel(acc)}</option>
            ))}
          </select>
        </div>
        {accountsError && (
          <p className="mt-2 text-xs text-red-600">Account list error: {accountsError}</p>
        )}
        {!currentNovaAccountId && configAccounts.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            Pick a Nova account above. The diaper-models catalog is fetched via that account's session.
          </p>
        )}
        {configAccounts.length === 0 && !accountsError && (
          <p className="mt-2 text-xs text-gray-500">
            No enabled Nova accounts configured. Add one in Ops UI → Configuration → Nova Accounts.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Controls */}
        <div className="space-y-6">
          
          {/* Base Model Selection */}
          <div className="bg-white rounded-lg shadow-md p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 border-b pb-2">1. Select Base Model</h3>
            <select
              className="w-full border border-gray-300 rounded p-2 text-sm focus:ring-2 focus:ring-blue-500"
              value={selectedBaseCode || ''}
              onChange={handleBaseModelChange}
            >
              {diaperModels.map(m => (
                <option key={m.code} value={m.code}>{m.name} (Code {m.code})</option>
              ))}
            </select>
            {baseModel && (
              <div className="mt-3 text-xs text-gray-600 bg-gray-50 p-2 rounded font-mono">
                <div>C0: {baseModel.c0.toExponential(4)}</div>
                <div>C1: {baseModel.c1.toExponential(4)}</div>
                <div>C2: {baseModel.c2.toExponential(4)}</div>
                <div>C3: {baseModel.c3.toExponential(4)}</div>
              </div>
            )}
          </div>

          {/* Experimental Coefficients */}
          <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-purple-500">
            <h3 className="text-lg font-semibold text-purple-800 mb-3 border-b pb-2">2. Tweak Polynomial</h3>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700">C0 (Constant)</label>
                <input 
                  type="number" step="any" value={c0} onChange={e => setC0(parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded p-1 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700">C1 (Linear - x)</label>
                <input 
                  type="number" step="any" value={c1} onChange={e => setC1(parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded p-1 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700">C2 (Quadratic - x²)</label>
                <input 
                  type="number" step="any" value={c2} onChange={e => setC2(parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded p-1 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700">C3 (Cubic - x³)</label>
                <input 
                  type="number" step="any" value={c3} onChange={e => setC3(parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded p-1 text-sm font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-700 mt-4">Capacity (mL)</label>
                <input 
                  type="number" value={capacity} onChange={e => setCapacity(parseFloat(e.target.value) || 0)}
                  className="w-full border border-gray-300 rounded p-1 text-sm font-mono"
                />
              </div>
            </div>
          </div>

          {/* Save Override */}
          <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-green-500">
            <h3 className="text-lg font-semibold text-green-800 mb-3 border-b pb-2">3. Save Local Override</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700">Override Name</label>
                <input 
                  type="text" value={expName} onChange={e => setExpName(e.target.value)}
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  placeholder="e.g. M-L4 (Aggressive Curve)"
                />
              </div>
              <button 
                onClick={handleSaveOverride}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded transition-colors"
              >
                💾 Save to Local Browser
              </button>
            </div>
          </div>

        </div>

        {/* Right Column: Chart */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow-md p-4 h-[500px] flex flex-col">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Polynomial Curve Comparison</h3>
            <div className="flex-1 relative">
              <ParentSize>
                {({ width, height }) => {
                  if (width === 0 || height === 0) return null;
                  
                  const margin = { top: 20, right: 20, bottom: 40, left: 50 };
                  const innerWidth = width - margin.left - margin.right;
                  const innerHeight = height - margin.top - margin.bottom;

                  const maxW = 32767;
                  
                  // Find max volume to scale Y axis appropriately (cap at 4000 or max capacity + 500)
                  const maxBaseV = Math.max(...baseData.map(d => d.v), 0);
                  const maxExpV = Math.max(...expData.map(d => d.v), 0);
                  const maxY = Math.min(4000, Math.max(maxBaseV, maxExpV, capacity + 200, (baseModel?.capacity || 0) + 200));

                  const xScale = scaleLinear({ domain: [0, maxW], range: [0, innerWidth] });
                  const yScale = scaleLinear({ domain: [0, maxY], range: [innerHeight, 0] });

                  const handleMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
                    const point = localPoint(e);
                    if (!point) return;
                    const x = point.x - margin.left;
                    const w = Math.max(0, Math.min(maxW, xScale.invert(x)));
                    
                    // Find closest point
                    const expPoint = expData.find(d => d.w >= w) || expData[expData.length-1];
                    const basePoint = baseData.find(d => d.w >= w) || baseData[baseData.length-1];

                    showTooltip({
                      tooltipData: { w: expPoint.w, baseV: basePoint.v, expV: expPoint.v },
                      tooltipLeft: xScale(expPoint.w) + margin.left,
                      tooltipTop: Math.min(yScale(basePoint.v), yScale(expPoint.v)) + margin.top,
                    });
                  };

                  return (
                    <>
                      <svg width={width} height={height}>
                        <Group left={margin.left} top={margin.top}>
                          <GridRows scale={yScale} width={innerWidth} height={innerHeight} stroke="#e0e0e0" />
                          <GridColumns scale={xScale} width={innerWidth} height={innerHeight} stroke="#e0e0e0" />
                          
                          {/* Base Model Line */}
                          <LinePath
                            data={baseData}
                            x={d => xScale(d.w)}
                            y={d => yScale(d.v)}
                            stroke="#94a3b8" // gray-400
                            strokeWidth={3}
                            strokeDasharray="5,5"
                            curve={curveMonotoneX}
                          />

                          {/* Experimental Model Line (Relevant Part) */}
                          <LinePath
                            data={expData.filter((d, i, arr) => {
                              if (d.v <= capacity) return true;
                              // Include the first point that exceeds capacity to prevent a gap
                              const prev = arr[i - 1];
                              return prev && prev.v <= capacity;
                            })}
                            x={d => xScale(d.w)}
                            y={d => yScale(d.v)}
                            stroke="#a855f7" // purple-500
                            strokeWidth={3}
                            curve={curveMonotoneX}
                          />

                          {/* Experimental Model Line (Irrelevant / Over Capacity Part) */}
                          <LinePath
                            data={expData.filter((d, i, arr) => {
                              if (d.v >= capacity) return true;
                              // Include the last point before exceeding capacity to prevent a gap
                              const next = arr[i + 1];
                              return next && next.v >= capacity;
                            })}
                            x={d => xScale(d.w)}
                            y={d => yScale(d.v)}
                            stroke="#d8b4fe" // purple-300 (faded)
                            strokeWidth={3}
                            strokeDasharray="4,4"
                            curve={curveMonotoneX}
                          />

                          {/* Max Capacity Line */}
                          <line
                            x1={0}
                            x2={innerWidth}
                            y1={yScale(capacity)}
                            y2={yScale(capacity)}
                            stroke="#ef4444" // red-500
                            strokeWidth={2}
                            strokeDasharray="6,4"
                          />
                          <text
                            x={10}
                            y={yScale(capacity) - 8}
                            fill="#ef4444"
                            fontSize={12}
                            fontWeight="bold"
                          >
                            Max Capacity ({capacity} mL)
                          </text>

                          {/* Intersection Point for Max Capacity */}
                          {(() => {
                            const intersectionPoint = expData.find(d => d.v >= capacity);
                            if (intersectionPoint) {
                              return (
                                <g>
                                  <line
                                    x1={xScale(intersectionPoint.w)}
                                    x2={xScale(intersectionPoint.w)}
                                    y1={yScale(capacity)}
                                    y2={innerHeight}
                                    stroke="#ef4444"
                                    strokeWidth={1}
                                    strokeDasharray="4,4"
                                    opacity={0.7}
                                  />
                                  <circle
                                    cx={xScale(intersectionPoint.w)}
                                    cy={yScale(capacity)}
                                    r={4}
                                    fill="#ef4444"
                                  />
                                  <text
                                    x={xScale(intersectionPoint.w) + 8}
                                    y={innerHeight - 10}
                                    fill="#ef4444"
                                    fontSize={11}
                                    opacity={0.8}
                                  >
                                    Wetness: {Math.round(intersectionPoint.w)}
                                  </text>
                                </g>
                              );
                            }
                            return null;
                          })()}

                          <AxisBottom 
                            scale={xScale} top={innerHeight} 
                            tickFormat={v => `${v}`}
                            label="Wetness Level"
                          />
                          <AxisLeft 
                            scale={yScale} 
                            label="Volume (mL)"
                          />

                          <rect
                            x={0} y={0} width={innerWidth} height={innerHeight}
                            fill="transparent"
                            onMouseMove={handleMouseMove}
                            onMouseLeave={hideTooltip}
                          />
                        </Group>
                      </svg>
                      
                      {tooltipData && (
                        <TooltipWithBounds top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
                          <div className="font-bold mb-1 border-b border-gray-600 pb-1">
                            Wetness: {Math.round(tooltipData.w)}
                          </div>
                          <div className="text-gray-300">
                            Base Volume: {Math.round(tooltipData.baseV)} mL
                          </div>
                          <div className="text-purple-300 font-bold">
                            Exp Volume: {Math.round(tooltipData.expV)} mL
                          </div>
                        </TooltipWithBounds>
                      )}
                    </>
                  );
                }}
              </ParentSize>
            </div>
            
            <div className="flex gap-4 justify-center mt-2 text-sm">
              <div className="flex items-center gap-1">
                <div className="w-4 h-1 border-b-2 border-dashed border-gray-400"></div>
                <span className="text-gray-600">Base Model ({baseModel?.name})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-1 bg-purple-500"></div>
                <span className="text-purple-700 font-medium">Experimental Model</span>
              </div>
            </div>
          </div>

          {/* List of saved overrides */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Saved Overrides</h3>
            {localModelOverrides.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No local overrides saved yet.</p>
            ) : (
              <div className="space-y-2">
                {localModelOverrides.map(override => (
                  <div key={override.code} className="flex justify-between items-center bg-gray-50 p-2 rounded border border-gray-200">
                    <div>
                      <span className="font-bold text-sm">{override.name}</span>
                      <span className="text-xs text-gray-500 ml-2">(Code: {override.code})</span>
                    </div>
                    <button 
                      onClick={() => deleteLocalOverride(override.code)}
                      className="text-red-500 hover:text-red-700 text-sm px-2 py-1 bg-red-50 hover:bg-red-100 rounded"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
